import { generateHarmonyPalette, normalizeHex } from '@/lib/studio-color';
import { fitOklch, parseColorInput } from '@/lib/color-input';
import { formatColor, hexToOklch } from '@/lib/color-formats';
import { isRecord } from '@/lib/persist';
import type { ColorFormat } from '@/lib/color-formats';
import type { PaletteComposerState, PaletteSize, SavedPalette, ThemeRoles } from '@/types/studio';

export const MIN_COLORS = 2;
export const MAX_COLORS = 12;
export const MAX_SAVED_PALETTES = 200;
export const paletteSize = (value: number): PaletteSize =>
  Math.max(MIN_COLORS, Math.min(MAX_COLORS, Math.round(value))) as PaletteSize;

export function regeneratePalette(
  state: PaletteComposerState,
  patch: Partial<PaletteComposerState> = {},
): PaletteComposerState {
  const next = { ...state, ...patch };
  const generated = generateHarmonyPalette(next);
  return {
    ...next,
    colors: generated.map((color, index) =>
      state.locked[index] && state.colors[index] ? state.colors[index]! : color,
    ),
    locked: generated.map((_, index) => state.locked[index] ?? false),
  };
}

export function resizePalette(state: PaletteComposerState, size: number): PaletteComposerState {
  const count = paletteSize(size);
  if (count === state.count) return state;
  const generated = generateHarmonyPalette({ ...state, count });
  return {
    ...state,
    count,
    colors: generated.map((color, index) => state.colors[index] ?? color),
    locked: generated.map((_, index) => state.locked[index] ?? false),
  };
}

export function randomizePalette(
  state: PaletteComposerState,
  random = Math.random,
): PaletteComposerState {
  if (state.locked.every(Boolean)) return state;
  const seed = fitOklch(0.5 + random() * 0.25, 0.1 + random() * 0.12, random() * 360).hex;
  return regeneratePalette(state, {
    seed,
    lightness: 0.55 + random() * 0.18,
    chroma: 0.1 + random() * 0.12,
  });
}

export function movePaletteColor(
  state: PaletteComposerState,
  from: number,
  to: number,
): PaletteComposerState {
  if (from < 0 || to < 0 || from >= state.count || to >= state.count || from === to) return state;
  const colors = [...state.colors];
  const locked = [...state.locked];
  colors.splice(to, 0, colors.splice(from, 1)[0]!);
  locked.splice(to, 0, locked.splice(from, 1)[0]!);
  return { ...state, colors, locked };
}

export function paletteFromColors(
  state: PaletteComposerState,
  colors: string[],
): PaletteComposerState {
  const count = paletteSize(colors.length);
  const normalized = colors.slice(0, count).map((color) => normalizeHex(color));
  if (normalized.length < MIN_COLORS) throw new Error('A palette needs at least two colors.');
  return {
    ...state,
    seed: normalized[0]!,
    count,
    colors: normalized,
    locked: normalized.map(() => false),
  };
}

export function createThemeRoles(colors: string[], dark = false): ThemeRoles {
  const primary = colors[0] ?? '#6D5DFC';
  const base = hexToOklch(primary);
  return {
    background: fitOklch(dark ? 0.16 : 0.98, 0.008, base.h).hex,
    surface: fitOklch(dark ? 0.22 : 1, 0.006, base.h).hex,
    text: fitOklch(dark ? 0.95 : 0.22, 0.015, base.h).hex,
    primary,
    secondary: colors[1] ?? primary,
    accent: colors[2] ?? primary,
  };
}

export function createColorScale(hex: string): Array<{ step: number; hex: string }> {
  const { c, h } = hexToOklch(hex);
  const levels = [0.98, 0.95, 0.89, 0.81, 0.72, 0.63, 0.54, 0.45, 0.36, 0.27, 0.19];
  return levels.map((l, index) => ({
    step: index === 0 ? 50 : index === 10 ? 950 : index * 100,
    hex: fitOklch(l, c * Math.sin((Math.PI * (index + 0.3)) / 11), h).hex,
  }));
}

export function serializePalette(
  colors: string[],
  name: string,
  format: 'css' | 'tailwind' | 'json' | 'svg',
  colorFormat: ColorFormat = 'hex',
): string {
  const formatted = colors.map((hex) => {
    const { l, c, h } = hexToOklch(hex);
    return colorFormat === 'hex' ? hex : formatColor(colorFormat, l, c, h);
  });
  if (format === 'json') return JSON.stringify({ name, colors }, null, 2);
  if (format === 'svg') {
    const escape = (text: string) =>
      text.replace(
        /[&<>"']/g,
        (s) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[s]!,
      );
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${colors.length * 160}" height="240" viewBox="0 0 ${colors.length * 160} 240"><title>${escape(name)}</title>${colors.map((hex, i) => `<rect x="${i * 160}" width="160" height="200" fill="${normalizeHex(hex)}"/><rect x="${i * 160}" y="200" width="160" height="40" fill="#FFFFFF"/><text x="${i * 160 + 80}" y="226" text-anchor="middle" font-family="monospace" font-size="14" fill="#111111">${normalizeHex(hex)}</text>`).join('')}</svg>`;
  }
  return [
    format === 'tailwind' ? '@theme {' : ':root {',
    ...formatted.map((color, i) => `  --color-palette-${i + 1}: ${color};`),
    '}',
  ].join('\n');
}

function parseColorList(value: unknown): string[] {
  if (!Array.isArray(value) || value.length < MIN_COLORS || value.length > MAX_COLORS)
    throw new Error('Use a palette with 2–12 colors.');
  return value.map((color) => {
    const parsed = typeof color === 'string' ? parseColorInput(color) : null;
    if (!parsed) throw new Error('A color is invalid. Use HEX, RGB, HSL, OKLab, or OKLCH.');
    return parsed.hex;
  });
}

export function parsePaletteImport(
  text: string,
): Array<{ name: string; colors: string[]; favorite?: boolean }> {
  if (text.length > 250_000)
    throw new Error('This palette file is too large. Use a file under 250 KB.');
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Paste some colors or choose a palette file.');
  let data: unknown;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      data = JSON.parse(trimmed);
    } catch {
      throw new Error('This JSON is not valid. Check brackets, commas, and quotes.');
    }
    if (isRecord(data) && Array.isArray(data.palettes)) {
      if (data.palettes.length < 1 || data.palettes.length > MAX_SAVED_PALETTES)
        throw new Error('Import 1–200 palettes at a time.');
      return data.palettes.map((entry) => {
        if (!isRecord(entry)) throw new Error('Each palette needs a name and a colors array.');
        return {
          name:
            typeof entry.name === 'string'
              ? entry.name.trim().slice(0, 80) || 'Imported palette'
              : 'Imported palette',
          colors: parseColorList(entry.colors),
          favorite: entry.favorite === true,
        };
      });
    }
    if (isRecord(data) && Array.isArray(data.colors))
      return [
        {
          name:
            typeof data.name === 'string'
              ? data.name.trim().slice(0, 80) || 'Imported palette'
              : 'Imported palette',
          colors: parseColorList(data.colors),
        },
      ];
    return [
      {
        name: 'Imported palette',
        colors: parseColorList(isRecord(data) ? Object.values(data) : data),
      },
    ];
  }
  const variables = [...trimmed.matchAll(/--[\w-]+\s*:\s*([^;{}]+);/g)];
  const tokens = /(?:oklch|oklab|rgba?|hsla?)\([^()]+\)|#?[0-9a-f]{6}\b|#[0-9a-f]{3}\b/gi;
  if (!variables.length && trimmed.replace(tokens, '').replace(/[\s,;]+/g, ''))
    throw new Error('Some text is not a color. Use a color list, CSS variables, or palette JSON.');
  const colors = variables.length ? variables.map((m) => m[1]!.trim()) : trimmed.match(tokens);
  return [{ name: 'Imported palette', colors: parseColorList(colors) }];
}

export function paletteShareHash(colors: string[], name: string): string {
  return `#${new URLSearchParams({ palette: colors.map((c) => normalizeHex(c).slice(1)).join('-'), name })}`;
}

export function parsePaletteHash(hash: string): { name: string; colors: string[] } | null {
  if (hash.length > 2000) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const raw = params.get('palette');
  if (!raw) return null;
  try {
    return {
      name: params.get('name')?.trim().slice(0, 80) || 'Shared palette',
      colors: parseColorList(raw.split('-').map((c) => `#${c}`)),
    };
  } catch {
    return null;
  }
}

export function makeSavedPalette(name: string, colors: string[]): SavedPalette {
  return {
    id: crypto.randomUUID(),
    name: name.trim().slice(0, 80) || 'Untitled palette',
    colors: [...colors],
    favorite: false,
    createdAt: new Date().toISOString(),
  };
}
